import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone, Mail, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useData } from "../context/DataContext";
import { resolveImageUrl } from "../admin/ImageUpload";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/cours-tenues", label: "Cours & Tenues" },
  { to: "/planning-tarifs", label: "Planning & Tarifs" },
  { to: "/inscription", label: "Inscription" },
  { to: "/galerie", label: "Galerie Photos" },
  { to: "/stages", label: "Stages" },

  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const { settings } = useData();
  const SITE = settings || {};
  const logo = resolveImageUrl(SITE.logo);
  const { count } = useCart();
  const shopOn = !!settings?.shopEnabled;
  const navLinks = shopOn ? [...links.slice(0, 4), { to: "/boutique", label: "Boutique" }, ...links.slice(4)] : links;
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 w-full bg-white border-b border-stone-100 transition-shadow duration-300 ${scrolled ? "shadow-sm" : ""}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          {logo ? (
            <img src={logo} alt="L'Élan Danse" className="h-14 md:h-16 w-auto object-contain" />
          ) : (
            <div className="flex flex-col leading-[0.85]">
              <span className="font-script text-4xl md:text-5xl text-deep-rose">L&apos;élan</span>
              <span className="font-script text-3xl md:text-4xl text-deep-rose self-end -mt-1">Danse</span>
            </div>
          )}
          <span className="hidden xl:block text-[10px] uppercase tracking-[0.2em] text-stone-500 leading-tight">École de danse<br/>Le Perray-en-Yvelines</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 text-stone-700">
          {shopOn && <Link to="/boutique/panier" data-testid="nav-cart-link" className="relative p-2 rounded-full hover:bg-blush-light hover:text-deep-rose transition-colors" aria-label="Panier">
            <ShoppingBag size={18} />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 bg-coral text-white text-[10px] leading-none h-4 min-w-4 px-1 rounded-full flex items-center justify-center">{count}</span>}
          </Link>}
          <a href={`tel:${(SITE.phone || "").replace(/\s/g, "")}`} className="p-2 rounded-full hover:bg-blush-light hover:text-deep-rose transition-colors" aria-label="Téléphone">
            <Phone size={18} />
          </a>
          <a href={`mailto:${SITE.email}`} className="p-2 rounded-full hover:bg-blush-light hover:text-deep-rose transition-colors" aria-label="Email">
            <Mail size={18} />
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-1">
        {shopOn && <Link to="/boutique/panier" data-testid="nav-cart-link-mobile" className="relative p-2 text-stone-800" aria-label="Panier">
          <ShoppingBag size={20} />
          {count > 0 && <span className="absolute top-0 right-0 bg-coral text-white text-[10px] leading-none h-4 min-w-4 px-1 rounded-full flex items-center justify-center">{count}</span>}
        </Link>}
        <button className="p-2 text-stone-800" onClick={() => setOpen((v) => !v)} aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-stone-100">
          <nav className="flex flex-col px-6 py-4 gap-4">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-3 pt-2 text-stone-700">
              <a href={`tel:${(SITE.phone || "").replace(/\s/g, "")}`} className="p-2 rounded-full bg-blush-light hover:text-deep-rose"><Phone size={18} /></a>
              <a href={`mailto:${SITE.email}`} className="p-2 rounded-full bg-blush-light hover:text-deep-rose"><Mail size={18} /></a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
